import axios from "axios";
import { toast } from "sonner";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const getSecureUrl = (url) => {
  if (process.env.NODE_ENV === "production" && url.startsWith("http://")) {
    return url.replace("http://", "https://");
  }
  return url;
};

// Create axios instance
const apiClient = axios.create({
  baseURL: getSecureUrl(`${API_BASE_URL}/api`),
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - modify URLs to HTTPS in production
apiClient.interceptors.request.use(
  (config) => {
    // Convert HTTP to HTTPS in production
    if (process.env.NODE_ENV === "production" && config.url) {
      config.url = config.url.replace("http://", "https://");
    }

    // Add JWT token if available
    const userStr = localStorage.getItem("hostelconnect_user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.access_token) {
          config.headers.Authorization = `Bearer ${user.access_token}`;
        }
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }

    // NODE_ENV
    if (process.env.NODE_ENV === "development") {
      console.log(
        `📤 API Request: ${config.method?.toUpperCase()} ${config.baseURL}${
          config.url
        }`,
        config.data
      );
    }

    return config;
  },
  (error) => {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // NODE_ENV
    if (process.env.NODE_ENV === "development") {
      console.log(
        `✅ API Response: ${response.config.method?.toUpperCase()} ${
          response.config.url
        }`,
        response.data
      );
    }

    return response;
  },
  async (error) => {
    console.error("❌ API Error:", error);

    // Handle different error scenarios
    if (error.code === "ECONNABORTED") {
      toast.error("⏱️ Request timeout. Please try again.");
      console.error("Timeout error:", error.config.url);
    } else if (error.code === "ERR_NETWORK") {
      toast.error("🌐 Network error. Please check your connection.");
      console.error("Network error - API might be down");
    } else if (error.response) {
      // Server responded with error
      const status = error.response.status;
      let rawDetail =
        error.response.data?.detail ||
        error.response.data?.message ||
        "An error occurred";

      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          if (json.detail) rawDetail = json.detail;
          else if (json.message) rawDetail = json.message;
        } catch (e) {
          // ignore parsing error
        }
      }

      let message = rawDetail;
      if (typeof rawDetail === "object" && rawDetail !== null) {
        if (Array.isArray(rawDetail)) {
          message = rawDetail[0]?.msg || rawDetail[0]?.detail || JSON.stringify(rawDetail);
        } else if (rawDetail.msg) {
          message = rawDetail.msg;
        } else {
          message = JSON.stringify(rawDetail);
        }
      }

      switch (status) {
        case 400:
          toast.error(message);
          break;
        case 401:
          toast.error("🔒 Session expired. Please login again.");
          localStorage.removeItem("hostelconnect_user");
          window.location.href = "/login";
          break;
        case 403:
          toast.error("⛔ Access forbidden. You don't have permission.");
          break;
        case 404:
          toast.error(message || "Requested resource not found.");
          break;
        case 500:
          toast.error("💥 Server error. Please try again later.");
          console.error("Server error details:", error.response.data);
          break;
        case 503:
          toast.error("🚧 Service unavailable. Please try again later.");
          console.error("Database connection issue");
          break;
        default:
          toast.error(message || `Error ${status}`);
      }

      console.error("Response error:", {
        status,
        url: error.config.url,
        method: error.config.method,
        data: error.response.data,
      });
    } else if (error.request) {
      // Request made but no response
      toast.error(
        "📡 No response from server. Please check if the backend is running."
      );
      console.error("No response received:", error.request);
    } else {
      // Something else happened
      toast.error(`❌ Error: ${error.message}`);
      console.error("Unknown error:", error.message);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
